import React, { useCallback, useEffect, useState } from 'react';
import { adminApiService } from '../../services/adminApi';
import './MaintenanceSiteToggle.css';

export type MaintenanceToggleVariant = 'compact' | 'card';

interface MaintenanceSiteToggleProps {
  variant?: MaintenanceToggleVariant;
  initialEnabled?: boolean | null;
  className?: string;
  /** Etiquetas cortas para la barra del admin (Activa / Manten.) */
  shortLabels?: boolean;
}

function ToggleButtons({
  isOn,
  loading,
  saving,
  onChange,
  compact,
  shortLabels,
}: {
  isOn: boolean;
  loading: boolean;
  saving: boolean;
  onChange: (next: boolean) => void;
  compact?: boolean;
  shortLabels?: boolean;
}) {
  return (
    <div
      className={
        compact
          ? 'maintenance-site-toggle__buttons maintenance-site-toggle__buttons--compact'
          : 'maintenance-site-toggle__buttons'
      }
    >
      <button
        type="button"
        className={
          !isOn
            ? 'maintenance-site-toggle__btn maintenance-site-toggle__btn--active'
            : 'maintenance-site-toggle__btn'
        }
        disabled={loading || saving || !isOn}
        onClick={() => onChange(false)}
        aria-pressed={!isOn}
      >
        {shortLabels ? 'Activa' : 'Web activa'}
      </button>
      <button
        type="button"
        className={
          isOn
            ? 'maintenance-site-toggle__btn maintenance-site-toggle__btn--warn maintenance-site-toggle__btn--active'
            : 'maintenance-site-toggle__btn maintenance-site-toggle__btn--warn'
        }
        disabled={loading || saving || isOn}
        onClick={() => onChange(true)}
        aria-pressed={isOn}
      >
        {shortLabels ? 'Manten.' : 'Web en mantenimiento'}
      </button>
    </div>
  );
}

const MaintenanceSiteToggle: React.FC<MaintenanceSiteToggleProps> = ({
  variant = 'compact',
  initialEnabled = null,
  className = '',
  shortLabels = false,
}) => {
  const [enabled, setEnabled] = useState<boolean | null>(initialEnabled);
  const [loading, setLoading] = useState(initialEnabled === null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const settings = await adminApiService.getSettings();
      setEnabled(settings.maintenance_enabled);
      try {
        const { writeMaintenanceCache } = await import('../../siteMaintenance');
        writeMaintenanceCache(settings.maintenance_enabled);
      } catch {
        // no-op
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo cargar el estado';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialEnabled !== null) {
      setEnabled(initialEnabled);
      setLoading(false);
      return;
    }
    loadStatus();
  }, [initialEnabled, loadStatus]);

  useEffect(() => {
    if (initialEnabled !== null) {
      setEnabled(initialEnabled);
    }
  }, [initialEnabled]);

  const onChange = async (next: boolean) => {
    if (saving || enabled === null || enabled === next) return;
    setSaving(true);
    setError(null);
    try {
      await adminApiService.setMaintenanceEnabled(next);
      setEnabled(next);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al guardar';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const isOn = Boolean(enabled);
  const rootClass = [
    'maintenance-site-toggle',
    `maintenance-site-toggle--${variant}`,
    isOn ? 'maintenance-site-toggle--maintenance' : 'maintenance-site-toggle--live',
    loading ? 'maintenance-site-toggle--loading' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (variant === 'card') {
    return (
      <div className={rootClass}>
        <div className="maintenance-site-toggle__card-header">
          <h3 className="maintenance-site-toggle__title">Estado de la web pública</h3>
          <span
            className={
              isOn
                ? 'maintenance-site-toggle__badge maintenance-site-toggle__badge--warn'
                : 'maintenance-site-toggle__badge maintenance-site-toggle__badge--ok'
            }
          >
            {loading ? 'Cargando…' : isOn ? 'En mantenimiento' : 'Web activa'}
          </span>
        </div>
        <p className="maintenance-site-toggle__desc">
          Activa el modo mantenimiento para mostrar la pantalla informativa a todos los visitantes.
          El panel <strong>/admin</strong> sigue disponible.
        </p>
        <ToggleButtons
          isOn={isOn}
          loading={loading}
          saving={saving}
          onChange={onChange}
          shortLabels={shortLabels}
        />
        {saving ? (
          <p className="maintenance-site-toggle__hint" aria-live="polite">
            Guardando…
          </p>
        ) : null}
        {error ? (
          <p className="maintenance-site-toggle__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className={rootClass} role="group" aria-label="Estado de la web pública">
      <span
        className={
          isOn
            ? 'maintenance-site-toggle__badge maintenance-site-toggle__badge--warn'
            : 'maintenance-site-toggle__badge maintenance-site-toggle__badge--ok'
        }
      >
        {loading ? '…' : isOn ? 'Mantenimiento' : 'Activa'}
      </span>
      <ToggleButtons
        isOn={isOn}
        loading={loading}
        saving={saving}
        onChange={onChange}
        compact
        shortLabels={shortLabels}
      />
      {saving ? (
        <span className="maintenance-site-toggle__hint" aria-live="polite">
          Guardando…
        </span>
      ) : null}
      {error ? (
        <span className="maintenance-site-toggle__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
};

export default MaintenanceSiteToggle;
